import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ProtectedEmail } from './ProtectedEmail';
import { Phone, Mail, MapPin, ExternalLink, Instagram } from 'lucide-react';

// Circular flag components with correct designs
const FlagIT = ({ className }) => (
  <svg className={className} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <circle cx="256" cy="256" r="256" fill="#F0F0F0"/>
    <path d="M512 256c0-110.1-69.7-204-167.5-240v480C442.3 460 512 366.1 512 256z" fill="#D80027"/>
    <path d="M0 256c0 110.1 69.7 204 167.5 240V16C69.7 52 0 145.9 0 256z" fill="#6DA544"/>
  </svg>
);

const FlagDE = ({ className }) => (
  <svg className={className} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <circle cx="256" cy="256" r="256" fill="#FFDA44"/>
    <path d="M15.9 166.9C5.6 194.4 0 224.5 0 256s5.6 61.6 15.9 89.1h480.2c10.3-27.5 15.9-57.6 15.9-89.1s-5.6-61.6-15.9-89.1H15.9z" fill="#D80027"/>
    <path d="M256 0C145.9 0 52 69.7 16 167.5h480C459.9 69.7 366.1 0 256 0z" fill="#333"/>
  </svg>
);

const FlagFR = ({ className }) => (
  <svg className={className} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <circle cx="256" cy="256" r="256" fill="#F0F0F0"/>
    <path d="M512 256c0-110.1-69.7-204-167.5-240v480C442.3 460 512 366.1 512 256z" fill="#D80027"/>
    <path d="M0 256c0 110.1 69.7 204 167.5 240V16C69.7 52 0 145.9 0 256z" fill="#0052B4"/>
  </svg>
);

const FlagGB = ({ className }) => (
  <svg className={className} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
    <circle cx="256" cy="256" r="256" fill="#F0F0F0"/>
    <g fill="#0052B4">
      <path d="M52.9 100.1c-20.1 26-35.5 55.9-44.9 88.5h118.2L52.9 100.1zM503.9 188.6c-9.4-32.6-24.8-62.5-44.9-88.5l-73.3 88.5H503.9zM8.1 323.4c9.4 32.6 24.8 62.5 44.9 88.5l73.3-88.5H8.1zM411.9 52.9c-26-20.1-55.9-35.5-88.5-44.9v118.2L411.9 52.9zM100.1 459.1c26 20.1 55.9 35.5 88.5 44.9V385.8L100.1 459.1zM188.6 8.1c-32.6 9.4-62.5 24.8-88.5 44.9l88.5 73.3V8.1zM323.4 503.9c32.6-9.4 62.5-24.8 88.5-44.9l-88.5-73.3V503.9zM385.8 323.4l73.3 88.5c20.1-26 35.5-55.9 44.9-88.5H385.8z"/>
    </g>
    <g fill="#D80027">
      <path d="M509.8 222.6H289.4V2.2C278.4.7 267.3 0 256 0s-22.4.7-33.4 2.2v220.4H2.2C.7 233.6 0 244.7 0 256s.7 22.4 2.2 33.4h220.4v220.4c11 1.4 22.1 2.2 33.4 2.2s22.4-.7 33.4-2.2V289.4h220.4c1.4-11 2.2-22.1 2.2-33.4s-.8-22.4-2.2-33.4z"/>
      <path d="M323.4 323.4L437 437c5.3-5.3 10.3-10.8 15.1-16.5L354 323.4h-30.6zM188.6 323.4l-113.6 113.6c5.3 5.3 10.8 10.3 16.5 15.1l97.1-98.1v-30.6zM188.6 188.6L75 75c-5.3 5.3-10.3 10.8-15.1 16.5l98.1 97.1h30.6zM323.4 188.6L437 75c-5.3-5.3-10.8-10.3-16.5-15.1l-97.1 98.1v30.6z"/>
    </g>
  </svg>
);

const flags = {
  it: FlagIT,
  de: FlagDE,
  fr: FlagFR,
  en: FlagGB,
};

const languages = [
  { code: 'it', label: 'IT' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
];

export const Footer = () => {
  const { t, language, setLanguage } = useLanguage();

  const quickLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/boats', label: t('nav.boats') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/events', label: t('nav.events') },
    { to: '/partners', label: t('nav.partners') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const services = [
    t('services.vendita.title'),
    t('services.invernaggio.title'),
    t('services.trasporto.title'),
    t('services.manutenzione.title'),
  ];

  return (
    <footer data-testid="footer" className="bg-[#0B1221] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_fd00ee9f-ec40-41d0-8fc4-33af1b0ace01/artifacts/445z42yf_LOGO_Brusa_BLACK_2000.png" 
                alt="Brusa Cantiere Nautico"
                className="h-16 w-auto object-contain brightness-0 invert"
              />
            </div>
            <p className="text-[#86868B] text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.to.replace('/', '') || 'home'}`}
                    className="text-[#86868B] hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6">
              {t('footer.ourServices')}
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to="/services"
                    className="text-[#86868B] hover:text-white transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-6">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <span className="text-[#86868B] text-sm">
                  Via Pizzante 11<br />
                  6595 Riazzino (Locarno)<br />
                  Svizzera
                </span>
              </li>
              <li>
                <a
                  href="tel:+41917921946"
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-[#86868B] hover:text-white transition-colors text-sm"
                >
                  <Phone className="w-4 h-4 text-[#FF3B30]" />
                  +41 91 792 19 46
                </a>
              </li>
              <li>
                <span className="flex items-center gap-3 text-[#86868B] hover:text-white transition-colors text-sm">
                  <Mail className="w-4 h-4 text-[#FF3B30]" />
                  <ProtectedEmail 
                    user="info" 
                    domain="nauticabrusa.ch" 
                    testId="footer-email"
                    className="hover:text-white transition-colors"
                  />
                </span>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/nauticabrusa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="flex items-center gap-3 text-[#86868B] hover:text-white transition-colors text-sm"
                >
                  <Instagram className="w-4 h-4 text-[#FF3B30]" />
                  @nauticabrusa
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar with Language Selector */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-[#86868B] text-sm">
              © {new Date().getFullYear()} Cantiere Nautico Brusa. {t('footer.rights')}
            </p>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2" data-testid="language-selector">
              {languages.map((lang) => {
                const FlagComponent = flags[lang.code];
                return (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                    className={`w-6 h-6 rounded-full overflow-hidden transition-all duration-200 ${
                      language === lang.code 
                        ? 'ring-2 ring-white ring-offset-1 ring-offset-[#0B1221] scale-110' 
                        : 'opacity-60 hover:opacity-100 hover:scale-105'
                    }`}
                    title={lang.code.toUpperCase()}
                  >
                    <FlagComponent className="w-full h-full" />
                  </button>
                );
              })}
            </div>
            
            <a
              href="https://nauticabrusa.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#86868B] hover:text-white transition-colors text-sm"
            >
              nauticabrusa.ch
            </a>
          </div>
          
          {/* Developer Credit */}
          <div className="mt-6 text-center">
            <p className="text-[#86868B]/60 text-xs">
              sviluppato da{' '}
              <a
                href="https://erunna.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
                data-testid="developer-credit"
              >
                ERUNNA SA
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
